import * as THREE from 'three';
import type { MapDefinition } from '../../../shared/game/world-types.js';
import { createCoastVisual, type MapVisual } from './coast-visual.js';
import { freezeSceneryTransforms } from '../scene-transforms.js';
import { batchSceneryShadows } from './scenery-shadows.js';
import { SceneryChunks } from './scenery-chunks.js';
import { createGroundGeometry } from './terrain-ground.js';
import { enableTerrainPicking, hasTerrainPicking } from './terrain-picking.js';

export function createMapVisual(scene: THREE.Scene, map: MapDefinition): MapVisual {
  const existing = new Set(scene.children);
  const visual = buildMapVisual(scene, map);
  if (map.terrain) enableTerrainPicking(visual.ground, map);
  tileGround(scene, visual.ground);
  const roots = scene.children.filter(root => !existing.has(root));
  for (const root of roots) freezeSceneryTransforms(root);
  batchSceneryShadows(scene, roots);
  const chunks = new SceneryChunks(scene, roots), grass = visual.grass;
  // Grass joins the scene after the still scenery is chunked: its tiles come and go with the view.
  if (grass) { scene.add(grass.group); grass.group.updateMatrixWorld(true); }
  visual.cull = (camera, sun) => { chunks.cull(camera, sun); grass?.stream(camera); };
  return visual;
}

/** Ground tile, in metres: the view and the sun's frustum each take in a handful. */
const GROUND_TILE = 40;
/**
 * The coast's ground is one mesh of about 2.8 million triangles across the whole map, which no frustum can cull: it was drawn in
 * full for the view and again for the shadow map every frame. This draws it as square tiles instead, each a mesh of its own
 * over the same vertex buffers (uploaded once) with the triangles whose middle falls inside it, so only the tiles in view or in
 * the sun's frustum draw. `ground` itself leaves the scene and stays the target for picking and the weather's wet look.
 */
function tileGround(scene: THREE.Scene, ground: THREE.Mesh) {
  const geometry = ground.geometry, position = geometry.attributes.position, index = geometry.index;
  if (!index || index.count < 30_000 || ground.parent !== scene) return;
  ground.updateMatrixWorld(true);
  const tiles = new Map<string, number[]>();
  for (let k = 0; k < index.count; k += 3) {
    const a = index.getX(k), b = index.getX(k + 1), c = index.getX(k + 2);
    const x = (position.getX(a) + position.getX(b) + position.getX(c)) / 3, z = (position.getZ(a) + position.getZ(b) + position.getZ(c)) / 3;
    const key = `${Math.floor(x / GROUND_TILE)}:${Math.floor(z / GROUND_TILE)}`;
    let list = tiles.get(key); if (!list) tiles.set(key, list = []); list.push(a, b, c);
  }
  for (const triangles of tiles.values()) {
    const part = new THREE.BufferGeometry();
    for (const [name, attribute] of Object.entries(geometry.attributes)) part.setAttribute(name, attribute);
    part.setIndex(new THREE.BufferAttribute(position.count > 65535 ? new Uint32Array(triangles) : new Uint16Array(triangles), 1));
    // Three bounds every vertex of a shared buffer; a tile is bounded by the corners of its own triangles.
    const box = new THREE.Box3(), corner = new THREE.Vector3();
    for (const vertex of triangles) box.expandByPoint(corner.fromBufferAttribute(position, vertex));
    part.boundingBox = box; part.boundingSphere = box.getBoundingSphere(new THREE.Sphere());
    const tile = new THREE.Mesh(part, ground.material);
    tile.name = 'ground-tile'; tile.castShadow = ground.castShadow; tile.receiveShadow = ground.receiveShadow; tile.renderOrder = ground.renderOrder;
    tile.position.copy(ground.position); tile.quaternion.copy(ground.quaternion); tile.scale.copy(ground.scale);
    scene.add(tile);
  }
  scene.remove(ground);
  // Lattice picking never reads the whole-map index, and the tiles carry their own: drop its copy (tens of megabytes on the coast).
  if (hasTerrainPicking(ground)) geometry.setIndex(null);
}

function buildMapVisual(scene: THREE.Scene, map: MapDefinition): MapVisual {
  if (map.scenery?.theme === 'sunset-coast') return createCoastVisual(scene, map);
  function box(width: number, height: number, depth: number, x: number, y: number, z: number, color: number) {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), new THREE.MeshStandardMaterial({ color, roughness: 1 }));
    mesh.position.set(x, y, z); mesh.castShadow = true; mesh.receiveShadow = true; scene.add(mesh);
  }
  box(map.width + 0.3, 0.7, map.depth + 0.3, 0, -0.4, 0, 0x293c3b);
  const ground = new THREE.Mesh(createGroundGeometry(map), new THREE.MeshStandardMaterial({ color: 0x57685b, roughness: 1 }));
  ground.receiveShadow = true; scene.add(ground);
  for (const road of map.roads) box(road.width, 0.015, road.depth, road.x, 0.015, road.z, 0x8b8b76);
  for (const obstacle of map.obstacles) {
    const { x, z, width, depth } = obstacle;
    if (obstacle.kind === 'ruin') {
      box(width, 0.18, depth, x, 0.1, z, 0x738078);
      box(width * 0.88, 1.25, depth * 0.88, x, 0.74, z, 0x8e9787);
      for (let stone = 0; stone < Math.ceil(width); stone++) box(0.75, 0.18, depth * 0.95, x - width / 2 + stone + 0.5, 1.46, z, 0xa6aa96);
    } else if (obstacle.kind === 'rock') {
      const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(0.85, 0), new THREE.MeshStandardMaterial({ color: 0x7b8982, flatShading: true }));
      rock.position.set(x, 0.6, z); rock.scale.set(width * 0.55, 0.8, depth * 0.6); rock.castShadow = true; scene.add(rock);
    } else {
      box(0.18, 1.6, 0.18, x, 0.8, z, 0x574c3b);
      const crown = new THREE.Mesh(new THREE.ConeGeometry(0.66, 1.9, 7), new THREE.MeshStandardMaterial({ color: 0x314d40, flatShading: true }));
      crown.position.set(x, 2.1, z); crown.castShadow = true; scene.add(crown);
    }
  }
  return { ground, update: () => {} };
}
